import { type FetchContext, type FetchRequest, type FetchResponse, type ResponseType } from 'ofetch'
import { ResponseCode } from '~/utils/constant'
import { timestampToDateTime, jufairLog, serializeErrorForLog } from '~/utils/common'
// import CryptoJS from 'crypto-js'
import HmacSHA256 from 'crypto-js/sha256'
import type { CookieRef } from '#app'
import { ELocale } from '~~/types/enums'
import { DistributionHttpHeaders, JUFAIR_PARTNER_REF_KEY, JUFAIR_SHARE_CHANNEL_KEY } from '~/utils/partnerRef'
import { appendLandingHeaders, getLandingSource } from '~/utils/landingSource'

// import { getLogger } from '~/utils/format';

export interface ResOptions<T> {
  data: T
  code: string
  message?: string
  errorMessage?: string
  informationSecret?: string
}

/**
 * SSR 直连后端地址，与 Nitro routeRules `/api/**` → `BASE_URL/**` 的路径规则一致。
 * 避免 SSR 走 `/api` 自代理回环（高峰时易超时，且 ALB 侧无访问日志）。
 */
export function resolveSsrApiUrl(url: string, baseUrl: string): string {
  if (!url.startsWith('/api/')) {
    return url
  }
  const path = url.replace(/^\/api/, '')
  return `${baseUrl.replace(/\/$/, '')}${path}`
}

export type HttpTimingPhase = 'request' | 'success' | 'requestError' | 'responseError'

/** 耗时落在哪一环节（用于和 ALB/Java 日志对照） */
export type HttpFailureStage =
  | 'OK'
  | 'TIMEOUT_NO_RESPONSE'
  | 'NETWORK_CONNECT'
  | 'GATEWAY_UPSTREAM'
  | 'HTTP_CLIENT_ERROR'
  | 'HTTP_SERVER_ERROR'
  | 'BUSINESS_ERROR'
  | 'UNKNOWN'

function inferFailureStage(
  meta: Record<string, any>,
  phase: HttpTimingPhase,
  ctx?: {
    error?: { name?: string; message?: string; cause?: { code?: string; message?: string } }
    response?: { status?: number }
  }
): HttpFailureStage {
  const duration = Date.now() - (meta.startTime || Date.now())
  const timeoutMs = meta.timeoutMs || 6000
  const status = ctx?.response?.status
  const err = ctx?.error
  const errMsg = err?.message || ''
  const causeCode = err?.cause?.code || ''

  if (phase === 'success') {
    const bizCode = ctx?.response?._data?.code
    if (bizCode !== undefined && bizCode != 1 && bizCode != 200 && String(bizCode) !== String(ResponseCode.SUCCESS)) {
      return 'BUSINESS_ERROR'
    }
    if (status && status >= 200 && status < 300) return 'OK'
    return status && status >= 500 ? 'HTTP_SERVER_ERROR' : 'HTTP_CLIENT_ERROR'
  }

  if (phase === 'requestError') {
    if (err?.name === 'AbortError' || /timeout|aborted/i.test(errMsg) || duration >= timeoutMs * 0.85) {
      return 'TIMEOUT_NO_RESPONSE'
    }
    if (['ECONNREFUSED', 'ENOTFOUND', 'ECONNRESET', 'EAI_AGAIN', 'ETIMEDOUT'].includes(causeCode)) {
      return 'NETWORK_CONNECT'
    }
    return 'NETWORK_CONNECT'
  }

  if (phase === 'responseError') {
    if (status === 502 || status === 503 || status === 504) return 'GATEWAY_UPSTREAM'
    if (status && status >= 500) return 'HTTP_SERVER_ERROR'
    if (status && status >= 400) return 'HTTP_CLIENT_ERROR'
    if (duration >= timeoutMs * 0.85) return 'TIMEOUT_NO_RESPONSE'
    return 'UNKNOWN'
  }

  return 'UNKNOWN'
}

function shouldLogHttpTiming(meta: Record<string, any>, phase: HttpTimingPhase, durationMs: number): boolean {
  if (!process.server) return false
  if (phase !== 'success') return true // 接口请求失败
  if (durationMs > 200) return true // 超时200毫秒
  const u = `${meta.logicalUrl || ''}${meta.resolvedUrl || ''}`
  return false // true
}

/**
 * 结构化 HTTP 耗时日志（仅 SSR 打）。
 * stage 对照：TIMEOUT_NO_RESPONSE=未收到响应(多为 ofetch 超时/回环排队)；NETWORK_CONNECT=连不上；
 * GATEWAY_UPSTREAM=已到网关(502/504)；OK+duration=后端往返耗时。
 */
export function logHttpTiming(
  meta: Record<string, any>,
  phase: HttpTimingPhase,
  ctx?: {
    error?: { name?: string; message?: string; cause?: { code?: string; message?: string } }
    response?: { status?: number; statusText?: string; _data?: { code?: string; message?: string } }
  }
) {
  const durationMs = meta.startTime ? Date.now() - meta.startTime : 0
  if (!shouldLogHttpTiming(meta, phase, durationMs)) return

  const stage = inferFailureStage(meta, phase, ctx)
  const payload = {
    action: 'httpTiming',
    phase,
    stage,
    stageHint: {
      TIMEOUT_NO_RESPONSE: '未收到HTTP响应，检查Nuxt超时/自代理排队/网络',
      NETWORK_CONNECT: 'TCP/DNS失败，请求未到达ALB',
      GATEWAY_UPSTREAM: '已到ALB/网关，上游(Java)慢或不可用',
      HTTP_SERVER_ERROR: 'HTTP 5xx，查ALB与Java',
      HTTP_CLIENT_ERROR: 'HTTP 4xx',
      BUSINESS_ERROR: 'HTTP 200 但业务 code 非成功',
      OK: '成功',
      UNKNOWN: '未知'
    }[stage],
    logicalUrl: meta.logicalUrl,
    resolvedUrl: meta.resolvedUrl,
    routeMode: meta.routeMode,
    durationMs,
    timeoutMs: meta.timeoutMs,
    httpStatus: ctx?.response?.status ?? null,
    bizCode: ctx?.response?._data?.code ?? null,
    errorName: ctx?.error?.name,
    errorMessage: ctx?.error?.message,
    errorCause: ctx?.error?.cause?.code || ctx?.error?.cause?.message,
    pageUrl: meta.pageUrl,
    pid: meta.pid
  }
  jufairLog(JSON.stringify(payload))
}

/** 为请求添加 trace 头（traceparent / X-Trace-Id / X-Span-Id），供 useHttp 及 handleRequest 复用 */
export function addTraceHeaders(ctx: FetchContext<any, ResponseType>) {
  const { options } = ctx
  const traceId = process.server
    ? (() => {
        try {
          const event = useRequestEvent()
          const span = event?.context?.__otelSpan
          return span ? span.spanContext().traceId : null
        } catch (error) {
          jufairLog(`[app/composables/httpHelper.ts:28],${JSON.stringify(serializeErrorForLog(error))}`)
          return null
        }
      })()
    : (useNuxtApp().payload as { traceId?: string }).traceId
  const spanId = process.server
    ? (() => {
        try {
          const event = useRequestEvent()
          const span = event?.context?.__otelSpan
          return span ? span.spanContext().spanId : null
        } catch (error) {
          jufairLog(`[app/composables/httpHelper.ts:39],${JSON.stringify(serializeErrorForLog(error))}`)
          return null
        }
      })()
    : (useNuxtApp().payload as { spanId?: string }).spanId
  if (traceId && spanId) {
    options.headers.set('traceparent', `00-${traceId}-${spanId}-01`)
    options.headers.set('X-Trace-Id', traceId)
    options.headers.set('X-Span-Id', spanId)
  }
}

// http请求拦截器
export function handleRequest(
  meta: Record<string, any>,
  token: CookieRef<string | undefined>,
  locale: keyof typeof ELocale,
  ctx: FetchContext<any, ResponseType>,
  opts: any
) {
  // 设置请求标头
  const { options } = ctx
  // options.body = JSON.stringify(options.body)

  // 删除NaN参数
  // for (let i in options.body as any) {
  //   if (typeof (options.body as any)[i] === 'number' && isNaN((options.body as any)[i])) {
  //     // 参数值是NaN，删除该参数
  //     delete (options.body as any)[i]
  //   }
  // }

  // changeSign(ctx, options, opts)
  // 仅将「对象/数组」序列化为 JSON。若 body 已是字符串，再 stringify 会变成带引号的 JSON 字符串，
  // 后端 Jackson 根节点按 String 反序列化，报 InnerChainInfo 无法从 String 构造（与 20002 一致）。
  {
    const raw = options.body
    if (raw !== undefined && raw !== null) {
      if (typeof raw === 'string') {
        options.body = raw
      } else if (
        typeof raw === 'object' &&
        !(raw instanceof FormData) &&
        !(raw instanceof Blob) &&
        !(typeof ArrayBuffer !== 'undefined' && raw instanceof ArrayBuffer) &&
        !(typeof URLSearchParams !== 'undefined' && raw instanceof URLSearchParams)
      ) {
        options.body = JSON.stringify(raw)
      }
    }
  }
  meta.startTime = Date.now()
  const reqUrl = String(meta.logicalUrl || ctx.request || '')
  // 对象存储不接受站点登录 token，带上会返回 400 Unsupported Authorization Type
  const isOssRequest = reqUrl.includes('ossApi')
  if (token.value && !isOssRequest) {
    options.headers.set('Authorization', token.value)
  }
  options.headers.set('NuxtTraceId', meta.startTime)
  options.headers.set('Locale', ELocale[locale])
  options.headers.set('DEVICE', 'pc')

  try {
    let pr: string | undefined
    /** 分销申请页等场景：以表单填写的推广码为准；空表示本次请求不带头（不沿用 cookie） */
    if (opts && Object.prototype.hasOwnProperty.call(opts, 'explicitPartnerRef')) {
      const ex = (opts as { explicitPartnerRef?: string | null }).explicitPartnerRef
      if (ex != null && String(ex).trim() !== '') {
        pr = String(ex).trim()
      }
    } else {
      const c = useCookie<string | null>(JUFAIR_PARTNER_REF_KEY)
      pr = c.value || undefined
      if (!pr && process.client && typeof localStorage !== 'undefined') {
        try {
          pr = localStorage.getItem(JUFAIR_PARTNER_REF_KEY) || undefined
        } catch {
          /* ignore */
        }
      }
    }
    if (pr) {
      options.headers.set(DistributionHttpHeaders.PARTNER_REF, pr)
    }
  } catch {
    /* ignore */
  }

  try {
    const scCookie = useCookie<string | null>(JUFAIR_SHARE_CHANNEL_KEY)
    let sc: string | undefined = scCookie.value || undefined
    if (!sc && process.client && typeof localStorage !== 'undefined') {
      try {
        sc = localStorage.getItem(JUFAIR_SHARE_CHANNEL_KEY) || undefined
      } catch {
        /* ignore */
      }
    }
    if (sc) {
      options.headers.set(DistributionHttpHeaders.SHARE_CHANNEL, sc)
    }
  } catch {
    /* ignore */
  }

  // 首次落地入口优先；没有缓存时退回当前页。登录、下单从这些 header 取来源。
  if (process.client && typeof window !== 'undefined') {
    try {
      const entry = getLandingSource()?.entryUrl || window.location?.href
      if (entry) {
        options.headers.set(DistributionHttpHeaders.PROMOTION_LINK, entry.slice(0, 500))
      }
      appendLandingHeaders(options.headers)
    } catch {
      /* ignore */
    }
  }

  // 透传 traceId 给 Java 等后端，便于前后端日志关联
  addTraceHeaders(ctx)
  options.headers.set('referer', 'https://www.jufair.com')
  options.headers.set('If-Modified-Since', '')
  if (!isOssRequest) {
    options.headers.set('Content-Type', 'application/json') // 2024-12-17
  }
  options.headers.set('x-meta-start-time', Date.now().toString()) //

  // 🔧 服务端请求时透传客户端 IP
  if (process.server) {
    try {
      const event = useRequestEvent()
      if (event) {
        const forwardedFor = event.node.req.headers['x-forwarded-for']
        const realIP = event.node.req.headers['x-real-ip']
        // 如果没有 X-Forwarded-For，则获取客户端 IP
        const clientIP = event.node.req.socket?.remoteAddress

        // 处理 header 值（可能是 string | string[] | undefined）
        const forwardedForStr = Array.isArray(forwardedFor) ? forwardedFor[0] : forwardedFor
        const realIPStr = Array.isArray(realIP) ? realIP[0] : realIP

        // 如果原始请求已有 X-Forwarded-For，直接透传（已包含完整的代理链）
        if (forwardedForStr) {
          const forwardedForValue = Array.isArray(forwardedFor) ? forwardedFor.join(', ') : forwardedFor || ''
          if (forwardedForValue) {
            options.headers.set('X-Forwarded-For', forwardedForValue)
          }
          // 如果有 X-Real-IP，也透传
          if (realIPStr) {
            options.headers.set('X-Real-IP', realIPStr)
          } else if (forwardedForStr) {
            // 从 X-Forwarded-For 提取第一个 IP（客户端真实 IP）
            const firstIP = forwardedForStr.split(',')[0]?.trim()
            if (firstIP) {
              options.headers.set('X-Real-IP', firstIP)
            }
          }
        } else {
          if (clientIP) {
            options.headers.set('X-Forwarded-For', clientIP)
            options.headers.set('X-Real-IP', clientIP)
          } else if (realIPStr) {
            // 如果只有 X-Real-IP，也透传
            options.headers.set('X-Real-IP', realIPStr)
            options.headers.set('X-Forwarded-For', realIPStr)
          }
        }
      }
    } catch (e) {
      // 静默处理错误，避免影响正常请求
      if (process.env.NODE_ENV === 'development') {
        console.warn('获取客户端 IP 失败:', e)
      }
    }
  }

  // 保存请求信息
  meta.url = ctx.request
  meta.method = options.method || 'GET'
  meta.referer = options.headers.get('referer')
  meta.data = options.body
  meta.traceId = meta.startTime
  meta.device = 'PC'
  meta.locale = ELocale[locale]

  if (process.env.NODE_ENV === 'development') {
    // console.log('响应:', ctx.response._data)
    console.log('请求拦截器--', ctx.request.split('?')[0])
    console.log('时间:', Date.now())
    console.log('参数:', options.body)
  }
  try {
    // 获取当前页面URL，使用请求上下文中的信息而不是 useRoute()
    const pageUrl = process.client ? window.location.href : ctx.request
    // console.log('页面url:', pageUrl)
    // console.log('67--', options.body)
    let bodyObject = options.body ? JSON.parse(options.body) : {}

    for (let i in bodyObject) {
      // console.log('24---', i, bodyObject[i])
      if (String(bodyObject[i]).includes('NaN')) {
        // 如果参数值是NaN
        // console.log('24---', i, bodyObject[i])
        // console.log(
        //   '参数值是NaN(PC-Nuxt3)',
        //   `页面url: ${pageUrl}, 接口: ${ctx.request}, 参数: ${options.body},出错参数: ${i}:${bodyObject[i]}`
        // )
        jufairLog(
          `参数值是NaN(PC-Nuxt3),页面url: ${pageUrl}, 接口: ${ctx.request}, 参数: ${options.body},出错参数: ${i}:${bodyObject[i]}`
        )
      }
    }
  } catch (e) {
    console.log('e:', e)
  }
}
// 接口验签
function changeSign(ctx: any, options: any, opts: any) {
  // ctx.request = ctx.request + '?name=lisi&x-nonce=zhangsan'
  // 加密
  let keyList = [
    {
      id: 1,
      key: 'mPm9eK3HpL1MT6QL32XadlbKNxYrzj5Eel22QuBfsIc='
    }
  ]
  let dataList = []
  if (options.body && typeof options.body === 'object') {
    for (let i in options.body) {
      dataList.push(`${i}=${options.body[i]}`)
    }
  }
  let dateStr = String(Date.now())
  options.headers.set('x-nonce', dateStr)
  options.headers.set('x-timestamp', dateStr)
  options.headers.set('x-key-version', keyList[0].id)

  let urlParams: any = {} // get请求的参数
  let urlParamsResult: any = '' // 排序后的get参数
  const apiUrl = ctx.request?.split('?')[0]
  let urlParamsStr: any = ctx.request?.includes('?') ? ctx.request?.split('?')[1] : '' // url参数字符串
  let urlParamsList: any = urlParamsStr ? urlParamsStr.split('&') : []
  urlParamsList.map((item: any) => {
    let kv = item.split('=')
    let k = kv[0]
    let v = kv[1]
    // urlParams[k] = v
    // 防止重复参数
    if (k != 'x-nonce' && k != 'x-timestamp' && k != 'x-key-version' && k != 'x-signature') {
      urlParams[k] = v
    }
  })
  let newHeader: any = {
    'x-nonce': dateStr,
    'x-timestamp': dateStr,
    ...opts.params,
    ...urlParams
    // "x-key-version": config.headers["x-key-version"]
  }

  //  // 排序后的url参数
  const paramList = Object.keys(urlParams)
    .sort()
    .map(k => `${k}=${urlParams[k]}`)
  urlParamsResult = paramList.length > 0 ? paramList.join('&') + '&' : '' // url参数
  let headerList = []
  // 排序后的url参数
  headerList = Object.keys(newHeader)
    .sort()
    .map(k => `${k}=${newHeader[k]}`)
  const newHeaderList = headerList.length > 0 ? headerList.join('&') : '' // url参数
  // for (let i in newHeader) {
  //     headerList.push(`${i}=${newHeader[i]}`);
  // }
  let contentType: any =
    !options.headers?.hasOwnProperty('Content-Type') ||
    (options.headers?.hasOwnProperty('Content-Type') &&
      options.headers.get('Content-Type').includes('application/json'))
  let bodyStr = contentType && options.body ? `&${JSON.stringify(options.body)}` : '' // post请求参数

  let messageStr = `${newHeaderList}${bodyStr}`
  let CryptoJSRes: any = HmacSHA256(messageStr, keyList[0].key).toString()
  options.headers.set('x-signature', CryptoJSRes)
  // url增加参数(加密参数)
  // if (ctx.request?.includes('?')) {
  //   ctx.request = `${ctx.request}&x-nonce=${dateStr}&x-timestamp=${dateStr}&x-key-version=${keyList[0].id}&x-signature=${CryptoJSRes}`
  // } else {
  //   ctx.request = `${ctx.request}?x-nonce=${dateStr}&x-timestamp=${dateStr}&x-key-version=${keyList[0].id}&x-signature=${CryptoJSRes}`
  // }
  ctx.request = `${apiUrl}?${urlParamsResult}x-nonce=${dateStr}&x-timestamp=${dateStr}&x-key-version=${keyList[0].id}&x-signature=${CryptoJSRes}`
}

// http响应拦截器
export function handleResponse(
  meta: Record<string, any>,
  token: CookieRef<string | undefined>,
  ctx: FetchContext<any, ResponseType> & {
    response: FetchResponse<any>
  }
) {
  // console.log('http响应拦截器--', token.value) //&& ctx.request.includes('/wiki/aliasList')
  // process.env.NODE_ENV === 'development'
  if (process.env.NODE_ENV === 'development') {
    console.log('PC-Nuxt4响应拦截器--', ctx.request)
    // console.log('url:', ctx.request) // ctx.request
    console.log('参数:', ctx.options.body)
    // console.log('httpStatus:', ctx.response.status)
    // console.log('响应:', ctx.response._data)
  }

  const { response, options } = ctx
  meta.endTime = Date.now()
  let duration = meta.endTime - meta.startTime
  meta.duration = duration
  try {
    const traceId = process.server
      ? (() => {
          try {
            const event = useRequestEvent()
            const span = event?.context?.__otelSpan
            return span ? span.spanContext().traceId : null
          } catch (error) {
            jufairLog(`[app/composables/httpHelper.ts:282],${JSON.stringify(serializeErrorForLog(error))}`)
            return null
          }
        })()
      : (useNuxtApp().payload as { traceId?: string }).traceId
    // response.headers.set('NuxtTraceId', traceId || '')
    response.headers['NuxtTraceId'] = traceId || ''
  } catch (e) {
    jufairLog(`[app/composables/httpHelper.ts:289],${JSON.stringify(serializeErrorForLog(e))}`)
  }
  // console.log('285--', response)
  // if (process.client && 'fundebug' in window) {
  //   console.log('fundebug--1测试上报')
  //   // ;(window as any).fundebug.notifyError(error)
  //   ;(window as any).fundebug.notify('RequestError', '测试http状态:status(PC-Nuxt3)')
  // }

  logHttpTiming(meta, 'success', { response: ctx.response })

  const url = (ctx.request || '') as string
  let apiCodeList = ['200015', '131002', '131003', '151002'] // 不提示错误消息
  // console.log('56--',response) // response._data?.hasOwnProperty("code")
  if (response._data?.hasOwnProperty('code') && response._data.code !== '1') {
    // 支付相关的接口，则不提示错误消息
    const payUrl = ['/getPayStatus', '/payStatus']
    const containsPath = payUrl.some(path => url.includes(path))
    if (containsPath) {
      return response
    }
    // if (response._data.errorMessage && !apiCodeList.includes(response._data.code)) {
    //   ElMessage({
    //     message: response._data.errorMessage,
    //     type: 'error',
    //     customClass: 'hello_message'
    //   })
    // }
    if (response._data?.errorMessage?.includes('接口验证失败')) {
      // console.log('签名验证失败')
      jufairLog(`签名验证失败`)
      // 签名验证失败
      if (process.server) {
        jufairLog('签名验证失败')
      } else {
        if ('fundebug' in window) {
          // ;(window as any).fundebug.notifyError(error)
          ;(window as any).fundebug.notify('RequestError', 'http状态:status(PC-Nuxt3)-拦截器199', {
            metaData: {
              // response,
              request: JSON.stringify({
                url: ctx.request
                // headers: error.response?.config.headers,
              })
              // data: JSON.stringify(error.response?.data),
              // responseHeaders: JSON.stringify(ctx.response.headers)
              // config,
            }
          })
        }
      }
    }

    // console.log('onResponse接口错误start------------------------------------------------------------')
    // ;(console.log(timestampToDateTime(Date.now())), console.log('onResponse =>')) // response._data
    // // console.dir(options)
    // console.log('url:', url)
    // console.log('method:', options.method)
    // // console.log('headers:', options.headers)
    // console.log('params:', options.body)
    // // console.log('message:', response._data)
    // console.log('onResponse接口错误end------------------------------------------------------------')
    let responseData = {
      time: timestampToDateTime(Date.now()),
      url: url,
      method: options.method,
      params: options.body,
      response: JSON.stringify(response)
    }
    jufairLog(`onResponse接口错误start:${JSON.stringify(responseData)}`)
  }

  if (process.server) {
    // getLogger().then(logger => {
    //   logger?.info(JSON.stringify(meta));
    // });
  }

  if (options.responseType === 'arrayBuffer') {
    return
  }

  if (response.status === 200 && response._data.code == ResponseCode.SUCCESS) {
    const urlPrefixes = ['/api/newsApi/get', '/api/exhibition/']
    if (urlPrefixes.some(prefix => url?.startsWith(prefix))) {
      // @ts-ignore
      response._data.informationSecret = response.headers['jufair-inforationsecret']
    }
  } else {
    if (
      [ResponseCode.AUTH_FAILED, ResponseCode.NO_LOGIN, ResponseCode.BLACK_LOGIN, ResponseCode.RESTART_LOGIN].includes(
        response._data.code
      )
    ) {
      token.value = undefined
      if (process.client) {
        const globalStore = useGlobalStore()
        // globalStore.loginModal?.open()
        const currentRoute = window.location.href
        window.location.href = `/login?redirect=${encodeURIComponent(currentRoute)}`
        // console.log('跳转登录', window.location.href)
      }
      return
    }

    // 微信二维码轮询, 不需要提示.
    if (
      /\/auth\/wxlogin\/.+/.test(url) ||
      /^\/v1/.test(url) ||
      url?.includes('ticketOrderApi/getPayStatus') ||
      url.includes('visaOrderApi/payStatus') ||
      url.includes('flightOrderApi/payStatus') ||
      url.includes('oss/info')
    )
      return

    if (url?.startsWith('/ossApi')) return

    const msg = response._data?.errorMessage || response._data?.message || '请求失败'
    let errorInfo = {
      url,
      method: options.method,
      params: options.body,
      errorMessage: msg
      //   headers: options.headers
    }

    if (process.server) {
      // console.log('95-95-',errorInfo) // 错误信息(接口,method, 参数)
      //   meta.errorMessage = msg;
      meta.errorMessage = JSON.stringify({ msg: msg, errorInfo: errorInfo })
      //   getLogger().then(logger => {
      //     logger?.info(JSON.stringify(meta));
      //   });
    } else if (process.client && !apiCodeList.includes(String(response._data?.code))) {
      // ElMessage.error(msg);
      ElMessage({
        message: msg,
        type: 'error',
        customClass: 'hello_message'
      })
    }
  }
}

// 异常处理
export function handleError(
  ctx: FetchContext<any, ResponseType> & {
    response: FetchResponse<any>
  },
  token: CookieRef<string | undefined>,
  meta: Record<string, any> = {}
) {
  // console.log('http错误拦截器--', `${token.value ? '有token' : '无token'}`)
  // console.log('拦截器-异常-114', ctx.error)
  const status = ctx.response.status || ''
  const message = ctx.response?._data?.message || ''

  if (process.env.NODE_ENV === 'development') {
    console.log('PC-Nuxt4错误拦截器--', ctx.request)
    // console.log('url:', ctx.request)
    console.log('httpStatus:', ctx.response.status)
    console.log('完整错误信息:', ctx.response)
  }
  logHttpTiming(meta, 'responseError', { error: ctx.error, response: ctx.response })
  if (process.client) {
    // ElMessage.error(ctx.response._data?.errorMessage || ctx.response._data?.message || '请求失败');
    ElMessage({
      message: ctx.response._data?.errorMessage || ctx.response._data?.message || '请求失败',
      type: 'error',
      customClass: 'hello_message'
    })
    // http状态不等于200时, 上报fundebug
    if (ctx.response.status != 200 && 'fundebug' in window) {
      // ;(window as any).fundebug.notifyError(error)
      ;(window as any).fundebug.notify(
        'RequestError',
        `${message}-${status}-接口响应错误-${token.value ? '有token' : '无token'}`,
        {
          metaData: {
            // response,
            request: JSON.stringify({
              url: ctx.request
              // headers: error.response?.config.headers,
            }),
            // data: JSON.stringify(error.response?.data),
            responseHeaders: JSON.stringify(ctx.response.headers),
            response: JSON.stringify(ctx.response)
            // config,
          }
        }
      )
    }
  }
}

// 请求错误处理
export function handleRequestError(
  ctx: FetchContext<any, ResponseType> & {
    request: FetchRequest
  },
  token: CookieRef<string | undefined>,
  meta: Record<string, any> = {}
  // ctx: FetchContext<any, FetchRequest>,
) {
  // console.log('http请求错误拦截器--', `${token.value ? '有token' : '无token'}`)
  // console.log(ctx.error)
  let errorMessage = ''
  let urlStr = ''
  if (ctx.request && typeof ctx.request === 'string') {
    urlStr = ctx.request?.split('?')[0]
  }
  errorMessage = `请求失败1002-${urlStr}`
  if (process.env.NODE_ENV === 'development') {
    console.log('http请求错误拦截器--', ctx)
    console.log('url:', ctx.request)
    console.log('参数:', ctx.options.body)
    console.log('error:', ctx.error)
    console.log('error-message:', ctx.error?.message)
    // console.log('error-stack:', ctx.error?.stack)
    // console.log('error--:', typeof ctx.error)
    // console.log('httpStatus:', ctx.response.status)
    // console.log('响应:', ctx.response)
  }
  logHttpTiming(meta, 'requestError', { error: ctx.error, response: (ctx as any).response })
  if (process.client) {
    // ElMessage.error(ctx.response._data?.errorMessage || ctx.response._data?.message || '请求失败');
    // ElMessage({
    //   message: errorMessage || '请求失败1002',
    //   type: 'error',
    //   customClass: 'hello_message'
    // })
    if ('fundebug' in window) {
      // ;(window as any).fundebug.notifyError(error)
      ;(window as any).fundebug.notify(
        'RequestError',
        `${errorMessage}-接口请求错误-${token.value ? '有token' : '无token'}`,
        {
          metaData: {
            // response,
            request: JSON.stringify({
              url: ctx.request,
              body: ctx.options.body,
              error: ctx.error
              // headers: error.response?.config.headers,
            })
            // config,
          }
        }
      )
    }
  }
}
